import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { tokens } from 'components/v3';
import { formatDate } from '../../Forms/shared/dates';
import Glyph from '../../Forms/Submissions/Glyph';
import { sx } from '../../Forms/Submissions/sx';
import { NAME_FIELD_ID, TIN_FIELD_ID } from '../shared/w9Template';
import { SectionLabel } from './AccessLog';

// "W-9s on file · all years" inside the entry card (design W9Parts.OnFile,
// w9-plan §6.3 item 9): the same driver's W-9s across tax years, newest
// first, with the one showing marked and a "View form" (the PDF) on each.
//
// The server has no "other years for this payee" query (payees match by
// the TIN hash, which never reaches the admin), so the dashboard feeds
// this with the rows it has loaded that look like the same payee:
// relatedEntries below (same masked TIN and same line-1 name, another
// tax year). The section renders only when there is more than one.

const key = (row) => {
  const a = (row && row.answers) || {};
  const tin = a[TIN_FIELD_ID] || {};
  const name = String(a[NAME_FIELD_ID] || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!name || !tin.last4) return null;
  return `${String(tin.kind || '').toUpperCase()}:${tin.last4}:${name}`;
};

// The entry plus every loaded row that looks like the same payee, one per
// tax year (the newest per year), newest year first.
export const relatedEntries = (rows, entry) => {
  const k = key(entry);
  if (!k) return [entry];
  const byYear = {};
  [entry, ...(rows || [])].forEach((row) => {
    if (!row || row.tax_year == null || key(row) !== k) return;
    const cur = byYear[row.tax_year];
    if (
      !cur ||
      (row.id !== entry.id &&
        cur.id !== entry.id &&
        new Date(row.submitted_at) > new Date(cur.submitted_at))
    ) {
      byYear[row.tax_year] = cur && cur.id === entry.id ? cur : row;
    }
  });
  return Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a)
    .map((y) => byYear[y]);
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => sx(p.theme).line};
  border-radius: 12px;
  background: ${(p) => sx(p.theme).card};
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 58px;
  padding: 10px 12px 10px 14px;
  background-image: ${(p) =>
    p.$on
      ? `linear-gradient(${sx(p.theme).tint}, ${sx(p.theme).tint})`
      : 'none'};

  & + & {
    border-top: 1px solid ${(p) => sx(p.theme).line};
  }
`;

const OnFileYears = ({ entries, activeId, currentYear, onPdf, isMobile }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <section
      aria-label="W-9s on file, all years"
      style={{ marginTop: isMobile ? 20 : 24 }}
      data-w9-on-file=""
    >
      <SectionLabel>W-9s on file · all years</SectionLabel>
      <List>
        {entries.map((entry) => {
          const on = entry.id === activeId;
          return (
            <Row key={entry.id} $on={on}>
              <span
                style={{
                  flex: 'none',
                  minWidth: 52,
                  fontSize: 18,
                  fontWeight: 800,
                  letterSpacing: '-0.3px',
                  color: t.text,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {entry.tax_year}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span
                  style={{ fontSize: 13.5, fontWeight: 600, color: t.text }}
                >
                  {entry.tax_year === currentYear
                    ? 'Current tax year'
                    : 'Archived · still on file'}
                  {on ? ' · showing' : ''}
                </span>
                <span
                  style={{
                    marginTop: 1,
                    fontSize: 12,
                    color: t.faint,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.reference} · e-signed {formatDate(entry.submitted_at)}
                </span>
              </span>
              <ButtonV2
                onClick={() => onPdf(entry)}
                icon={<Glyph name="file-text" size={14} />}
                style={{ width: 'auto', flex: 'none' }}
              >
                View form
              </ButtonV2>
            </Row>
          );
        })}
      </List>
    </section>
  );
};

OnFileYears.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      reference: PropTypes.string.isRequired,
      tax_year: PropTypes.number,
      submitted_at: PropTypes.string,
    })
  ).isRequired,
  activeId: PropTypes.number,
  currentYear: PropTypes.number.isRequired,
  onPdf: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

OnFileYears.defaultProps = { activeId: null, isMobile: false };

export default OnFileYears;
